<template>
    <div class="min-h-screen bg-gray-900 text-white py-3 px-2 sm:py-6 sm:px-4 lg:px-8">
        <div class="max-w-4xl mx-auto">
            <div class="shadow-lg rounded-lg overflow-hidden bg-gray-800 p-3 sm:p-6 hover:shadow-md">
                <div class="flex items-center justify-between mb-4 sm:mb-6">
                    <h2 class="text-xl sm:text-2xl font-semibold text-white">System Settings</h2>
                </div>

                <form @submit.prevent="handleSubmit" class="space-y-4 sm:space-y-6">
                    <!-- Telegram Token -->
                    <div>
                        <label for="telegramToken" class="block text-sm font-medium text-gray-200">
                            Telegram Token
                        </label>
                        <input type="text" id="telegramToken" v-model="form.telegramToken"
                            class="mt-1 p-2.5 sm:p-2 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] touch-manipulation" />
                        <p class="mt-1 text-xs text-gray-400">Telegram bot token for notifications (login alerts, system
                            messages)
                        </p>
                    </div>

                    <!-- Chat IDs -->
                    <div>
                        <label for="chatIds" class="block text-sm font-medium text-gray-200">
                            Chat IDs (Press Shift + Enter for multiple IDs)
                        </label>
                        <div class="mt-1 space-y-2">
                            <input id="chatIds" v-model="chatIdInput" @keydown="handleChatIdKeydown"
                                class="p-2.5 sm:p-2 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] touch-manipulation" />
                            <div v-if="form.chatIds.length > 0" class="flex flex-wrap gap-2 mt-2">
                                <div v-for="(id, index) in form.chatIds" :key="index"
                                    class="inline-flex items-center px-3 py-1 rounded-full bg-blue-600 border border-blue-500 text-blue-100 hover:bg-blue-700 transition-colors duration-200">
                                    <span class="text-sm font-medium mr-2 break-all">{{ id }}</span>
                                    <button type="button" @click="removeChatId(index)"
                                        class="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-blue-800 text-blue-200 hover:text-blue-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400">
                                        <X class="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <p class="mt-1 text-xs text-gray-400">Telegram chat IDs for notifications
                        </p>
                    </div>







                    <div class="flex justify-end space-x-3">
                        <button type="submit" :disabled="isLoading"
                            class="cursor-pointer inline-flex justify-center rounded-md border border-transparent bg-blue-600 hover:bg-blue-700 px-6 py-3 sm:px-4 sm:py-2 text-base sm:text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 min-h-[44px] min-w-[120px] touch-manipulation">
                            {{ isLoading ? "Saving..." : "Save Changes" }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Success Toast -->
    <ToastedVue v-if="showToast" :informMessage="toastMessage" :isFailed="toastFailed"
        @onAutoClose="handleCloseToast" />
</template>

<script setup>
import { ref, onMounted } from "vue";
import {
    doc,
    setDoc,
    serverTimestamp,
    collection,
    query,
    getDocs,
    limit,
} from "firebase/firestore";
import { projectFirestore } from "@/firebase/config";
import { getAuth } from "firebase/auth";
import ToastedVue from "../components/ToastedVue.vue";
import { X } from "lucide-vue-next";


const form = ref({
    telegramToken: "",
    chatIds: [],
    createdAt: null,
    createdBy: null,
});

const chatIdInput = ref("");
const isLoading = ref(false);
const showToast = ref(false);
const toastMessage = ref("");
const toastFailed = ref(false);

const handleChatIdKeydown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        if (chatIdInput.value.trim()) {
            form.value.chatIds.push(chatIdInput.value.trim());
            chatIdInput.value = "";
        }
    }
};

const removeChatId = (index) => {
    form.value.chatIds.splice(index, 1);
};

const handleSubmit = async () => {
    try {
        isLoading.value = true;
        // Add any remaining chat IDs in the input
        if (chatIdInput.value.trim()) {
            form.value.chatIds.push(chatIdInput.value.trim());
            chatIdInput.value = "";
        }

        // Update the settings document
        if (form.value._docRef) {
            await setDoc(form.value._docRef, {
                telegramToken: form.value.telegramToken,
                chatIds: form.value.chatIds,
                updatedAt: serverTimestamp(),
                createdAt: form.value.createdAt || serverTimestamp(),
                createdBy: form.value.createdBy || null,
            }, { merge: true });

            showToast.value = true;
            toastMessage.value = "Settings updated successfully!";
            toastFailed.value = false;
        } else {
            // If no document reference exists, create a new one
            const auth = getAuth();
            const currentUser = auth.currentUser;
            const settingsCollection = collection(projectFirestore, "settings");
            const newSettingsDocRef = doc(settingsCollection);

            await setDoc(newSettingsDocRef, {
                telegramToken: form.value.telegramToken,
                chatIds: form.value.chatIds,
                createdAt: serverTimestamp(),
                createdBy: currentUser ? currentUser.uid : null,
                updatedAt: serverTimestamp(),
            });

            // Store the doc ref for future updates
            form.value._docRef = newSettingsDocRef;

            showToast.value = true;
            toastMessage.value = "Settings created and saved successfully!";
            toastFailed.value = false;
        }
    } catch (error) {
        console.error("Error updating settings:", error);
        showToast.value = true;
        toastMessage.value = error.message || "Failed to update settings";
        toastFailed.value = true;
    } finally {
        isLoading.value = false;
    }
};

const loadSystemSettings = async () => {
    try {
        // Fetch only the first settings document with limit(1)
        const settingsCollection = collection(projectFirestore, "settings");
        const q = query(settingsCollection, limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const settingsDoc = querySnapshot.docs[0];
            const data = settingsDoc.data();
            form.value = {
                telegramToken: data.telegramToken || "",
                chatIds: data.chatIds || [],
                createdAt: data.createdAt || null,
                createdBy: data.createdBy || null,
                _docRef: settingsDoc.ref
            };

        } else {
            // No document found, create a new one
            const auth = getAuth();
            const currentUser = auth.currentUser;

            const newSettingsDocRef = doc(settingsCollection);
            const newSettingsData = {
                telegramToken: "",
                chatIds: [],
                createdAt: serverTimestamp(),
                createdBy: currentUser ? currentUser.uid : null,
            };

            await setDoc(newSettingsDocRef, newSettingsData);

            form.value = {
                telegramToken: "",
                chatIds: [],
                createdAt: newSettingsData.createdAt,
                createdBy: newSettingsData.createdBy,
                _docRef: newSettingsDocRef
            };

        }
    } catch (error) {
        console.error("Error loading settings:", error);
        showToast.value = true;
        toastMessage.value = "Failed to load settings";
        toastFailed.value = true;
    }
};

const handleCloseToast = () => {
    showToast.value = false;
};

onMounted(() => {
    loadSystemSettings();
});
</script>
